

import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Padding scale for Card's three slots.
 *
 * `md` (p-4) is the default because it is what the app's own card containers
 * actually draw — 49 files hand-roll a `rounded-xl bg-surface-2 … p-3/p-4` box
 * rather than import this component, and the shadcn-inherited `p-6` was half
 * the reason why (the other half was `rounded-2xl`; see Card below). `lg` keeps
 * the old value as an explicit opt-in for form-shaped cards.
 */
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

const CARD_PADDING: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outline' | 'glass' | 'interactive' | 'parchment';
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', ...props }, ref) => {
    const variantClasses = {
      default: 'bg-surface-2 border border-surface-3',
      elevated: 'bg-surface-2 shadow-xl shadow-black/30 border border-surface-3',
      outline: 'bg-transparent border-2 border-surface-4',
      glass: 'bg-surface-2/80 backdrop-blur-xl border border-surface-3/50',
      interactive: 'bg-surface-2 border border-surface-3 hover:border-surface-4 transition-all duration-200 cursor-pointer',
      parchment: 'bg-parchment border border-parchment-border shadow-xl shadow-black/10',
    };

    // The app draws card containers at rounded-xl (203 usages vs 42 for 2xl).
    // `parchment` is held back at 2xl on purpose: it renders a certificate face
    // rather than a UI container, and it is the only variant with live
    // consumers (the four (auth)/* pages), which must not shift. Computed
    // rather than layered as a base + variant override because tailwind-merge
    // only de-dupes conflicting radii when both are classes it recognises.
    const radiusClass = variant === 'parchment' ? 'rounded-2xl' : 'rounded-xl';

    return (
      <div
        ref={ref}
        className={cn(
          radiusClass,
          'text-cream',
          variantClasses[variant],
          className
        )}
        {...props}
      />
    );
  }
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { padding?: CardPadding }
>(({ className, padding = 'md', ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5', CARD_PADDING[padding], className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('font-bold text-lg leading-none tracking-tight text-cream', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-warm-gray', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

// `pt-0` assumes a CardHeader sits above and has already paid the top padding.
// Kept as-is: dropping it would add a top gutter to every existing consumer.
const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { padding?: CardPadding }
>(({ className, padding = 'md', ...props }, ref) => (
  <div
    ref={ref}
    className={cn(CARD_PADDING[padding], padding !== 'none' && 'pt-0', className)}
    {...props}
  />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { padding?: CardPadding }
>(({ className, padding = 'md', ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center',
      CARD_PADDING[padding],
      padding !== 'none' && 'pt-0',
      className
    )}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
